import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Generic CRUD page — renders a list + add/edit form for any resource.
 * Props:
 *   API_BASE     — base URL (e.g., http://localhost:5000/api)
 *   endpoint     — resource path (e.g., 'leaves', 'payslips')
 *   title        — page heading
 *   fields       — array of { key, label, type?, required?, options? }
 *   listColumns  — array of field keys to show in table (defaults to all)
 *   idField      — default '_id'
 */
export default function GenericCrudPage({
  API_BASE,
  endpoint,
  title,
  fields = [],
  listColumns,
  idField = '_id',
  emptyMessage = 'No records yet. Click "Add" to create one.',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem('dentalToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const url = `${API_BASE}/${endpoint}`;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(url, { headers });
      const list = Array.isArray(data) ? data : (data.items || data.data || data.results || []);
      setItems(list);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const resetForm = () => {
    const empty = {};
    fields.forEach(f => { empty[f.key] = f.default ?? ''; });
    setForm(empty);
    setEditingId(null);
  };

  const openAdd = () => { resetForm(); setShowForm(true); };
  const openEdit = (item) => {
    const data = {};
    fields.forEach(f => { data[f.key] = item[f.key] ?? ''; });
    setForm(data);
    setEditingId(item[idField]);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.filter(f => f.required && !form[f.key]).map(f => f.label);
    if (missing.length) return alert(`Required: ${missing.join(', ')}`);
    try {
      if (editingId) {
        await axios.put(`${url}/${editingId}`, form, { headers });
      } else {
        await axios.post(url, form, { headers });
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axios.delete(`${url}/${id}`, { headers });
      load();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const cols = listColumns || fields.map(f => f.key);
  const filtered = search
    ? items.filter(item => JSON.stringify(item).toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6 }}
          />
          <button
            onClick={openAdd}
            style={{ background: '#4F46E5', color: '#fff', padding: '8px 16px', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            + Add
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : filtered.length === 0 ? (
        <div style={{ background: '#F9FAFB', padding: 40, textAlign: 'center', borderRadius: 8, color: '#6B7280' }}>
          {emptyMessage}
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {cols.map(c => (
                  <th key={c} style={{ padding: 12, textAlign: 'left', fontSize: 13, color: '#374151', borderBottom: '1px solid #E5E7EB' }}>
                    {fields.find(f => f.key === c)?.label || c}
                  </th>
                ))}
                <th style={{ padding: 12, textAlign: 'right', width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr key={item[idField]} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  {cols.map(c => {
                    const val = item[c];
                    const display = typeof val === 'object' ? JSON.stringify(val) : (val ?? '—');
                    return (
                      <td key={c} style={{ padding: 12, fontSize: 14 }}>
                        {String(display).substring(0, 80)}
                      </td>
                    );
                  })}
                  <td style={{ padding: 12, textAlign: 'right' }}>
                    <button onClick={() => openEdit(item)} style={{ marginRight: 8, padding: '4px 10px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => remove(item[idField])} style={{ padding: '4px 10px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <form onSubmit={submit} style={{ background: '#fff', padding: 24, borderRadius: 8, width: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit' : 'Add'} {title}</h3>
            {fields.map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 4 }}>
                  {f.label}{f.required && <span style={{ color: '#DC2626' }}> *</span>}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    value={form[f.key] || ''}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    rows={3}
                    style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 4 }}
                  />
                ) : f.type === 'select' ? (
                  <select
                    value={form[f.key] || ''}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 4 }}
                  >
                    <option value="">-- select --</option>
                    {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type={f.type || 'text'}
                    value={form[f.key] || ''}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 4 }}
                  />
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
              <button type="submit" style={{ padding: '8px 16px', background: '#4F46E5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{editingId ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
